// NEW: UI_REVAMP
import { Box, Flex, Grid, useDisclosure } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { Fragment, useEffect, useState } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { getTicketlistWithFilterApi } from '../api/query/betApi.js';
import DesktopPagination, {
	buildCompactPaginationItems,
	buildPaginationItems,
} from '../components/common/DesktopPagination';
import EmptyState from '../components/common/EmptyState';
import SearchFilterHeader from '../components/common/SearchFilterHeader';
import AppliedHistoryFilters from '../components/history/filter/AppliedHistoryFilters';
import HistoryList from '../components/history/HistoryList';
import { getVoidActionEnabled } from '../components/history/void/voidButtonUtils';
import {
	HistoryFilterPanel,
	MobileHistoryFilterSheet,
} from '../components/modal/filterRecord/HistoryFilterPanels';
import { ContainerLoadingModal } from '../components/modal/GlassModal';
import useIsMobileView from '../hooks/useIsMobileView';

import { currencyFormat } from '../utils/numberFormat.js';
import Time, { ConvertedTime } from '../utils/Time.js';

const HISTORY_RESET_FILTERS_ON_RETURN_KEY = 'historyResetFiltersOnReturn';

const getHistoryDate = (date) => (date ? new Date(date) : new Date());

const getRequestTicketStatus = ({ isTicketNumberSearch, ticketStatus }) => {
	if (isTicketNumberSearch || ticketStatus === 'All') {
		return '';
	}

	if (ticketStatus === 'Voidable') {
		return 'Posted';
	}

	return ticketStatus;
};

const HistoryScreen = () => {
	const navigate = useNavigate();

	const isMobileView = useIsMobileView();
	const { serverTime } = Time();
	const [date, setDate, maxDate] = useOutletContext();
	const shouldResetFiltersOnReturn =
		sessionStorage.getItem(HISTORY_RESET_FILTERS_ON_RETURN_KEY) === 'true';
	const getInitialHistoryDate = () => getHistoryDate(shouldResetFiltersOnReturn ? null : date);
	// state
	const [isLoading, setIsLoading] = useState(true);
	const [siteFilter, setSiteFilter] = useState('All');
	const [ticketStatus, setTicketStatus] = useState('All');
	const [searchValue, setSearchValue] = useState('');
	const [inputValue, setInputValue] = useState('');
	const [desktopPageNum, setDesktopPageNum] = useState(1);
	const [desktopPageSize, setDesktopPageSize] = useState(3);
	const [mobilePageNum, setMobilePageNum] = useState(1);
	const [mobilePageSize, setMobilePageSize] = useState(3);
	const [initialHistoryDate, setInitialHistoryDate] = useState(getInitialHistoryDate);
	const [filterStartDate, setFilterStartDate] = useState(getInitialHistoryDate);
	const [filterEndDate, setFilterEndDate] = useState(getInitialHistoryDate);
	const [hasAppliedDateRange, setHasAppliedDateRange] = useState(false);

	const { isOpen, onOpen, onClose } = useDisclosure();
	const maxSelectableDate = new Date(new Date(maxDate).setDate(new Date(maxDate).getDate() + 1));
	const drawDateFrom = dayjs(filterStartDate || date).format('YYYY-MM-DD HH:mm:ss');
	const drawDateTo = dayjs(filterEndDate || date).format('YYYY-MM-DD HH:mm:ss');
	const isTicketNumberSearch = Boolean(inputValue.trim());
	const requestDrawDateFrom = isTicketNumberSearch ? '' : drawDateFrom;
	const requestDrawDateTo = isTicketNumberSearch ? '' : drawDateTo;
	const requestSiteName = isTicketNumberSearch || siteFilter === 'All' ? '' : siteFilter;
	const requestTicketStatus = getRequestTicketStatus({ isTicketNumberSearch, ticketStatus });
	//* api
	const getDesktopTicketlistWithFilter = useQuery(
		[
			'getTicketlistWithFilterApi',
			'desktop',
			requestDrawDateFrom,
			requestDrawDateTo,
			requestSiteName,
			requestTicketStatus,
			inputValue,
			desktopPageNum,
			desktopPageSize,
		],
		() =>
			getTicketlistWithFilterApi({
				drawDateFrom: requestDrawDateFrom,
				drawDateTo: requestDrawDateTo,
				ticketNumber: inputValue,
				siteName: requestSiteName,
				ticketStatus: requestTicketStatus,
				pageParam: desktopPageNum,
				pageSize: desktopPageSize,
			}),
		{
			enabled: !isMobileView && Boolean(filterStartDate || date) && Boolean(filterEndDate || date),
			keepPreviousData: true,
			refetchOnWindowFocus: false,
		},
	);

	const getMobileTicketlistWithFilter = useQuery(
		[
			'getTicketlistWithFilterApi',
			'mobile',
			requestDrawDateFrom,
			requestDrawDateTo,
			requestSiteName,
			requestTicketStatus,
			inputValue,
			mobilePageNum,
			mobilePageSize,
		],
		() =>
			getTicketlistWithFilterApi({
				drawDateFrom: requestDrawDateFrom,
				drawDateTo: requestDrawDateTo,
				ticketNumber: inputValue,
				siteName: requestSiteName,
				ticketStatus: requestTicketStatus,
				pageParam: mobilePageNum,
				pageSize: mobilePageSize,
			}),
		{
			enabled: isMobileView && Boolean(filterStartDate || date) && Boolean(filterEndDate || date),
			keepPreviousData: true,
			refetchOnWindowFocus: false,
		},
	);

	// function
	const handleClearInput = () => {
		const today = new Date();

		setSearchValue('');
		setInputValue('');
		setInitialHistoryDate(today);
		setFilterStartDate(today);
		setFilterEndDate(today);
		setHasAppliedDateRange(false);
		setDate(today);
	};

	const handleSearch = () => {
		const nextInputValue = searchValue.trim();

		if (!nextInputValue) {
			handleClearInput();
			return;
		}

		setSearchValue(nextInputValue);
		setInputValue(nextInputValue);
	};

	const handleApplyHistoryDateRange = (start, end) => {
		const nextStartDate = start || initialHistoryDate;
		const nextEndDate = end || nextStartDate;
		const isCustomDateRange =
			!dayjs(nextStartDate).isSame(initialHistoryDate, 'day') ||
			!dayjs(nextEndDate).isSame(initialHistoryDate, 'day');

		setFilterStartDate(nextStartDate);
		setFilterEndDate(nextEndDate);
		setHasAppliedDateRange(isCustomDateRange);
		setDate(nextEndDate);
	};

	const handleClearDateRange = () => {
		setFilterStartDate(initialHistoryDate);
		setFilterEndDate(initialHistoryDate);
		setHasAppliedDateRange(false);
		setDate(initialHistoryDate);
	};

	// useEffect
	useEffect(() => {
		setDesktopPageNum(1);
	}, [filterStartDate, filterEndDate, siteFilter, ticketStatus, inputValue, desktopPageSize]);

	useEffect(() => {
		setMobilePageNum(1);
	}, [filterStartDate, filterEndDate, siteFilter, ticketStatus, inputValue, mobilePageSize]);

	useEffect(() => {
		if (!hasAppliedDateRange) {
			const nextDate = getHistoryDate(date);
			setInitialHistoryDate(nextDate);
			setFilterStartDate(nextDate);
			setFilterEndDate(nextDate);
		}
	}, [date, hasAppliedDateRange]);

	useEffect(() => {
		const shouldResetFilters =
			sessionStorage.getItem(HISTORY_RESET_FILTERS_ON_RETURN_KEY) === 'true';

		if (!shouldResetFilters) return;

		const resetDate = new Date();

		sessionStorage.removeItem(HISTORY_RESET_FILTERS_ON_RETURN_KEY);
		setSiteFilter('All');
		setTicketStatus('All');
		setSearchValue('');
		setInputValue('');
		setDesktopPageNum(1);
		setMobilePageNum(1);
		setHasAppliedDateRange(false);
		setInitialHistoryDate(resetDate);
		setFilterStartDate(resetDate);
		setFilterEndDate(resetDate);
		setDate(resetDate);
	}, [setDate]);

	useEffect(() => {
		if (
			(!isMobileView && getDesktopTicketlistWithFilter.isSuccess) ||
			(isMobileView && getMobileTicketlistWithFilter.isSuccess)
		) {
			setIsLoading(false);
		} else {
			setIsLoading(true);
		}
	}, [
		getDesktopTicketlistWithFilter.isSuccess,
		getMobileTicketlistWithFilter.isSuccess,
		isMobileView,
	]);

	const filterDisplayedHistoryByStatus = (items) => {
		if (isTicketNumberSearch) {
			return items;
		}

		if (ticketStatus === 'Posted') {
			return items.filter((item) => !getVoidActionEnabled(item, serverTime));
		}

		if (ticketStatus === 'Voidable') {
			return items.filter((item) => getVoidActionEnabled(item, serverTime));
		}

		return items;
	};

	const desktopContent = filterDisplayedHistoryByStatus(
		getDesktopTicketlistWithFilter.data?.content || [],
	);
	const desktopPaging = getDesktopTicketlistWithFilter.data?.paging || {};
	const desktopHasHistoryContent = desktopContent.length > 0;
	const desktopTotalPages = Math.max(desktopPaging?.totalPages || 1, 1);
	const desktopTotalEntries =
		desktopPaging?.totalElements ||
		desktopPaging?.totalCount ||
		desktopPaging?.totalRecords ||
		desktopPaging?.totalItems ||
		desktopContent.length;
	const desktopStartEntry = desktopTotalEntries ? (desktopPageNum - 1) * desktopPageSize + 1 : 0;
	const desktopEndEntry = desktopTotalEntries
		? Math.min(desktopPageNum * desktopPageSize, desktopTotalEntries)
		: 0;
	const desktopPaginationItems = buildPaginationItems(desktopPageNum, desktopTotalPages);
	const mobileContent = filterDisplayedHistoryByStatus(
		getMobileTicketlistWithFilter.data?.content || [],
	);
	const mobilePaging = getMobileTicketlistWithFilter.data?.paging || {};
	const mobileHasHistoryContent = mobileContent.length > 0;
	const mobileTotalPages = Math.max(mobilePaging?.totalPages || 1, 1);
	const mobileTotalEntries =
		mobilePaging?.totalElements ||
		mobilePaging?.totalCount ||
		mobilePaging?.totalRecords ||
		mobilePaging?.totalItems ||
		mobileContent.length;
	const mobileStartEntry = mobileTotalEntries ? (mobilePageNum - 1) * mobilePageSize + 1 : 0;
	const mobileEndEntry = mobileTotalEntries
		? Math.min(mobilePageNum * mobilePageSize, mobileTotalEntries)
		: 0;
	const mobilePaginationItems = buildCompactPaginationItems(mobilePageNum, mobileTotalPages, 3);
	const isHistoryFetching = isMobileView
		? getMobileTicketlistWithFilter.isFetching
		: getDesktopTicketlistWithFilter.isFetching;

	return (
		<>
			<Box
				display='flex'
				flexDirection='column'
				flex='1'
				position='relative'
				width='100%'
				paddingTop={'0'}
			>
				<Box position='relative' width='100%'>
					<SearchFilterHeader
						searchValue={searchValue}
						onChangeValue={(e) => {
							e.target.value = e.target.value.replace(/[^0-9]/g, '');
							setSearchValue(e.target.value);
							if (e.target.value === '') {
								handleClearInput();
							}
						}}
						onSearch={handleSearch}
						onClearSearch={handleClearInput}
						onFilterClick={() => {
							onOpen();
							if (!isMobileView && window.smartsupp) {
								window.smartsupp(function () {
									const container = document.getElementById('smartsupp-widget-container');
									const widget = document.getElementById('widgetButtonFrame');

									if (container?.firstElementChild) {
										container.firstElementChild.style.zIndex = '999';
									}
									if (widget?.firstElementChild) {
										widget.firstElementChild.style.zIndex = '999';
									}
								});
							}
						}}
					/>
					{!isMobileView ? (
						<HistoryFilterPanel
							isOpen={isOpen}
							onClose={onClose}
							siteFilter={siteFilter}
							setSiteFilter={setSiteFilter}
							ticketStatus={ticketStatus}
							setTicketStatus={setTicketStatus}
							selected={initialHistoryDate}
							startDate={filterStartDate}
							endDate={filterEndDate}
							onChangeDateRange={handleApplyHistoryDateRange}
							maxDate={maxSelectableDate}
						/>
					) : null}
				</Box>

				<ContainerLoadingModal
					isOpen={isHistoryFetching}
					inset={{ base: -2, lg: -4 }}
					borderRadius={{ base: 'inherit', lg: '16px' }}
				/>

				{isLoading ? null : (
					<>
						{isMobileView ? (
							<MobileHistoryFilterSheet
								isOpen={isOpen}
								onClose={onClose}
								siteFilter={siteFilter}
								setSiteFilter={setSiteFilter}
								ticketStatus={ticketStatus}
								setTicketStatus={setTicketStatus}
								selected={initialHistoryDate}
								startDate={filterStartDate}
								endDate={filterEndDate}
								onChangeDateRange={handleApplyHistoryDateRange}
								maxDate={maxSelectableDate}
							/>
						) : null}

						<Flex flexDirection='column' marginBottom='0' flex='1' minHeight='0'>
							{!isTicketNumberSearch ? (
								<AppliedHistoryFilters
									startDate={filterStartDate}
									endDate={filterEndDate}
									ticketStatus={ticketStatus}
									siteFilter={siteFilter}
									onClearDateRange={handleClearDateRange}
									onClearTicketStatus={() => setTicketStatus('All')}
									onClearSite={() => setSiteFilter('All')}
								/>
							) : null}

							{!isMobileView ? (
								<>
									{desktopHasHistoryContent ? (
										<>
											<Grid
												alignItems='center'
												justifyContent='center'
												width='100%'
												templateColumns='repeat(1, 1fr)'
											>
												{desktopContent.map((items, idx) => {
													return (
														<HistoryList
															key={`${items.ticketId}-${idx}`}
															status={items.status}
															ticketId={items.ticketNumber}
															timeData={ConvertedTime({
																time: items.date,
																format: 'DD/MM/YYYY hh:mm A',
															})}
															drawDT={ConvertedTime({
																time: items.drawTime,
																format: 'DD/MM/YYYY hh:mm A',
															})}
															gameType={items.siteName}
															betTotal={currencyFormat(items.totalBetPrice ?? 0)}
															betCount={items.bettingCount}
															winTtl={`${items.winningCount}/${items.bettingCount}`}
															amountTtl={items.winningPrice}
															isExpire={items.isExpire}
															ticket={items}
															serverTime={serverTime}
															userName={items.firstName}
															mobileNo={items.mobileNo}
															userType={items.isAgent ? 'Agent' : 'Player'}
															onClick={() => {
																sessionStorage.setItem('accessTicketId', items.ticketId);
																navigate('/history/ticketdetail');
															}}
														/>
													);
												})}
											</Grid>
											<DesktopPagination
												pageNum={desktopPageNum}
												pageSize={desktopPageSize}
												totalEntries={desktopTotalEntries}
												startEntry={desktopStartEntry}
												endEntry={desktopEndEntry}
												totalPages={desktopTotalPages}
												paginationItems={desktopPaginationItems}
												isFetching={getDesktopTicketlistWithFilter.isFetching}
												onPageChange={setDesktopPageNum}
												onPageSizeChange={setDesktopPageSize}
											/>
										</>
									) : (
										<EmptyState />
									)}
								</>
							) : mobileHasHistoryContent ? (
								<>
									<Grid
										alignItems='center'
										justifyContent='center'
										width='100%'
										templateColumns='repeat(1, 1fr)'
									>
										{mobileContent.map((items, idx) => {
											return (
												<Fragment key={`${items.ticketId}-${idx}`}>
													<HistoryList
														status={items.status}
														ticketId={items.ticketNumber}
														timeData={ConvertedTime({
															time: items.date,
															format: 'DD/MM/YYYY hh:mm A',
														})}
														drawDT={ConvertedTime({
															time: items.drawTime,
															format: 'DD/MM/YYYY hh:mm A',
														})}
														gameType={items.siteName}
														betTotal={currencyFormat(items.totalBetPrice ?? 0)}
														betCount={items.bettingCount}
														winTtl={`${items.winningCount}/${items.bettingCount}`}
														amountTtl={items.winningPrice}
														isExpire={items.isExpire}
														ticket={items}
														serverTime={serverTime}
														userName={items.firstName}
														mobileNo={items.mobileNo}
														userType={items.isAgent ? 'Agent' : 'Player'}
														onClick={() => {
															sessionStorage.setItem('accessTicketId', items.ticketId);
															navigate('/history/ticketdetail');
														}}
													/>
												</Fragment>
											);
										})}
									</Grid>
									<DesktopPagination
										pageNum={mobilePageNum}
										pageSize={mobilePageSize}
										totalEntries={mobileTotalEntries}
										startEntry={mobileStartEntry}
										endEntry={mobileEndEntry}
										totalPages={mobileTotalPages}
										paginationItems={mobilePaginationItems}
										isFetching={getMobileTicketlistWithFilter.isFetching}
										onPageChange={setMobilePageNum}
										onPageSizeChange={setMobilePageSize}
									/>
								</>
							) : (
								<EmptyState />
							)}
						</Flex>
					</>
				)}
			</Box>
		</>
	);
};

export default HistoryScreen;
